import React, {useRef, useState} from "react";
import {OTPFormContainer, OTPInput} from "./styles";

const inputs = [...Array(6).keys()];

interface IProps {
    value: string;
    onChange: (val: string) => void;
    handleSubmit?: () => void;
    error?: boolean;
    disabled?: boolean;
    autoSubmit?: boolean;
}

const OTPInputForm = (props: IProps) => {
    const [autoSubmitTriggered, setAutoSubmitTriggered] = useState(false);
    const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

    const isDigit = (char: string) => {
        const regex = new RegExp(/^\d+$/);
        return regex.test(char);
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>, i: number) => {
        const char = e.target.value.substring(0, 1);

        if (!isDigit(char)) return;

        const valueArr = [...props.value];
        valueArr[i] = char;
        const value = valueArr.join('');
        props.onChange(value);
        
        if (props.autoSubmit && !autoSubmitTriggered && value.length === 6) {
            props.handleSubmit();
            setAutoSubmitTriggered(true);
        } else {
            focusToInputByIndex(i + 1);
        }
    }

    const handleMouseDown = (e: React.MouseEvent<HTMLInputElement>, i: number) => {
        e.preventDefault();
        if (props.value.length >= i) {
            focusToInputByIndex(i);
            if (props.error) {
                props.onChange('');
            }
        }
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, i: number) => {
        const key = e.key;
        const target = e.target as HTMLInputElement;

        if (key === 'ArrowRight' || key === 'ArrowDown') {
            e.preventDefault();
            const nextInput = target.nextElementSibling as HTMLInputElement;
            if (nextInput && target.value !== '') {
                focusToInputByIndex(i + 1);
            }
        }

        if (key === 'ArrowLeft' || key === 'ArrowUp') {
            e.preventDefault();
            focusToInputByIndex(i - 1);
        }

        if (key === 'Backspace') {
            e.preventDefault();
            const nextInput = target.nextElementSibling as HTMLInputElement;
            if (!nextInput || nextInput.value === '') {
                const value = props.value.slice(0, -1);
                props.onChange(value);
                const inputIndexToFocus = target.value !== '' ? i : i - 1;
                focusToInputByIndex(inputIndexToFocus);
            }
        }

        if (key === 'Enter') {
            e.preventDefault();
            if (props.value.length === 6) {
                props.handleSubmit();
            }
        }
    }

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>, i: number) => {
        e.preventDefault();
        const pastedValue = e.clipboardData.getData("text/plain");

        if (!isDigit(pastedValue)) return;

        const newValue = props.value
            .substring(0, i)
            .concat(pastedValue)
            .substring(0, 6);

        props.onChange(newValue);
        focusToInputByIndex(newValue.length);
    }

    const focusToInputByIndex = (targetIndex: number) => {
        const index = targetIndex < 0 ? 0 : targetIndex >= 6 ? 5 : targetIndex;

        if (inputRefs.current[index]) {
            inputRefs.current[index].focus();
            inputRefs.current[index].setSelectionRange(0, 0);
        }
    }
    
    const setElementRef = (el: HTMLInputElement, i: number) => {
        inputRefs.current[i] = el;
        if (el) {
            el.style.setProperty('color', '#444444', 'important');
            el.setAttribute('inputmode', 'numeric');
        }
    }

    return (
        <OTPFormContainer>
            {inputs.map((i) => (
                <OTPInput
                    key={i}
                    value={props.value[i] || ""}
                    error={props.error}
                    ref={(el: HTMLInputElement) => setElementRef(el, i)}
                    onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => handlePaste(e, i)}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, i)}
                    onMouseDown={(e: React.MouseEvent<HTMLInputElement>) => handleMouseDown(e, i)}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => handleKeyDown(e, i)}
                    disabled={props.disabled}
                    autoFocus={i === 0}
                    type="numeric"
                    inputmode="numeric"
                />
            ))}
        </OTPFormContainer>
    );
}

export default OTPInputForm;
